import type { ReactNode } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { usePrivacyStore } from '../../store/privacyStore'
import { BLUR_PLACEHOLDER } from '../../../../../packages/policy/sensitive-fields.js'

interface SensitiveProps {
  /** Sensitive-field tag from packages/policy/sensitive-fields.js */
  tag?: string
  /**
   * The value to render. If the server already redacted it (replaced with
   * the blur placeholder or omitted the key), we just pass through. The
   * only client-side transform is the global Privat-Modus override.
   */
  children?: ReactNode
  /** Additional classes on the outer span. */
  className?: string
}

/**
 * Display wrapper for values that MAY be sensitive.
 *
 * The authoritative masking is SERVER-SIDE — the backend either emits the
 * raw value (show), a placeholder `••••` (blur), or omits the key (hide).
 * This wrapper does two things:
 *   1. Reads the global Privat-Modus flag — when on, replace any value
 *      with the blur placeholder so the screen is safe to show others.
 *   2. Adds an accessible label so screen readers announce "maskiert"
 *      instead of the placeholder characters.
 *
 * The transition between masked and revealed states uses a spring Y-slide
 * + blur ramp so toggling Privat-Modus feels physical rather than digital.
 * Respects `prefers-reduced-motion` automatically.
 *
 * Never use this to HIDE a value that the server would otherwise include —
 * the raw bytes already crossed the wire. Put the fence at the API.
 */
export function Sensitive({ children, className = '' }: SensitiveProps) {
  const privacy = usePrivacyStore(s => s.privacyMode)

  const raw = children
  const isMasked = privacy || raw === BLUR_PLACEHOLDER || raw === null || raw === undefined

  return (
    <span className={`relative inline-block align-baseline ${className}`}>
      <AnimatePresence mode="wait" initial={false}>
        {isMasked ? (
          <motion.span
            key="masked"
            initial={{ y: 6, opacity: 0, filter: 'blur(6px)' }}
            animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
            exit={{ y: -6, opacity: 0, filter: 'blur(6px)' }}
            transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.6 }}
            className="inline-block font-mono tabular-nums text-slate-400 tracking-widest select-none"
            aria-label="Feld maskiert"
            role="text"
          >
            {BLUR_PLACEHOLDER}
          </motion.span>
        ) : (
          <motion.span
            key="raw"
            initial={{ y: 6, opacity: 0, filter: 'blur(6px)' }}
            animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
            exit={{ y: -6, opacity: 0, filter: 'blur(6px)' }}
            transition={{ type: 'spring', stiffness: 420, damping: 28, mass: 0.6 }}
            className="inline-block"
          >
            {raw}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  )
}
